import _ from "lodash";
import { MutableRefObject, useContext, useEffect, useRef } from "react";
import TextBox from "../../../../ui/TextBox";
import InlineInputControlRow from "../../../../ui/layout/InlineInputControlRow";
import Label from "../../../../ui/Label";
import MultilineTextBox from "../../../../ui/MultilineTextBox";
import SecondaryButton from "../../../../ui/SecondaryButton";
import Select from "../../../../ui/Select";
import { useTypedFastStorage } from "../../../../hooks/useFastStorage";
import ApplicationContext from "../../../../ApplicationContext";
import { focusTabableByIndex } from "../../../../util/inputUtils";
import useGlobalOkDialog from "../../../../hooks/useGlobalOkDialog";
import LeftRightSplit from "../../../../ui/LeftRightSplit";
import { CustomizeReport } from "../../../../fms.core/CustomizeReport";
import { TrackableEntity } from "../../../../fms.core/data/ChangeTrackable";
import useDirtyCheck from "../../../../hooks/useDirtyCheck2";
import useSetEditedId from "../../../../hooks/useSetEditedId";
import { JobNames } from "../../../../framework.core/JobNames";
import { ApiResult, InsertResultsWithData } from "../../../../framework.core/data/ApiResult";
import { SQLInfo } from "../../../../framework.core/data/SQLInfo";
import { FormType } from "../../../../fms.enum";
import { now } from "../../../../dateUtils";
import { getOriginalValue, getSQLInfoList } from "../../../../fms.core/data/TrackingModule";
import { historyUpdate } from "../../../../fms.data/modules/FmsModFunc";
import { appendLine } from "../../../../util";

interface SpecialReportStorageKeys {
  modelStorage: string,
};

interface SpecialReportViewParams {
  id: string,
  onPopupClose?: () => void,
  mode: FormType,
};

interface SpecialReportLoadedDetailsViewParams {
  customizeReport: CustomizeReport,
  customizeReportRef: MutableRefObject<CustomizeReport|null>,
  setSpecialReport: (x: Partial<CustomizeReport>) => void,
  onPopupClose?: () => void,
  isView: boolean,
  save: () => Promise<boolean>,
};

function getSpecialReportStorageKeys(id: string, mode: FormType): SpecialReportStorageKeys {
  return {modelStorage: `specialReport-${mode}-${id}`}
}

function SpecialReportImpl(params: SpecialReportViewParams) {
  const {id, mode, onPopupClose} = params;
  const {modelStorage} = getSpecialReportStorageKeys(id, mode);
  const appContext = useContext(ApplicationContext);
  const [inStorage, setInStorage, removeFromStorage, inStorageRef] = useTypedFastStorage<CustomizeReport>(modelStorage, null);
  const {setCleanState, } = useDirtyCheck(modelStorage);
  const parsedId = !!id ? parseInt(id) : 0;
  const setSavedEntity = useSetEditedId([JobNames.Query]);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const _sqlInfosRef = useRef<SQLInfo[]>([]);
  const isView = mode === FormType.ModeView;

  useEffect(() => {
    initialize();
    return () => {
      removeFromStorage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadExisting() {
    const apiResult = await appContext.api.getJobDetail(JobNames.Query, {id: parsedId})
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult ? <p>{apiResult.message}</p> : <p>Query not found.</p>);
      if (onPopupClose) onPopupClose();
      return;
    }
  
    const query = new CustomizeReport(apiResult.result);
    setCleanState(query);
    setInStorage(_.cloneDeep(query));
    if (inStorageRef.current !== null) inStorageRef.current.trackable = true;
  }

  async function initialize() {
    if (!inStorage) {
      if (mode === FormType.ModeAdd) {
        const newQueryModel = new CustomizeReport();
        const newModel = _.cloneDeep(newQueryModel);
        setInStorage(newModel);
        setCleanState(newQueryModel);
    
        if (inStorageRef.current) {
          inStorageRef.current.trackable = true;
          setInStorage({
            tbName: 'Query',
            query: '',
          });
    
          TrackableEntity.acceptChanges(inStorageRef.current, modelStorage);
        }
      } else {
        await loadExisting();
      }
    }
  }

  async function saveQuery():Promise<ApiResult<InsertResultsWithData> | null> { 
    if (isView) 
      return null;

    if (!inStorage || !inStorageRef.current) 
      return null;

    if(!await checkDataAsync()) return null;

    _sqlInfosRef.current.splice(0);
    const userID = appContext.user ? appContext.user.id ?? '' : '';
    const additionalChange: Partial<CustomizeReport> = {};

    const currentTimestamp = now();

    if (mode === FormType.ModeAdd) {
      additionalChange.u1ID = userID;
      additionalChange.u1Date = currentTimestamp;
    }
    additionalChange.u2ID = userID;
    additionalChange.u2Date = currentTimestamp;

    additionalChange.querySub1 = inStorageRef.current.query.substring(0, 3500);
    additionalChange.querySub2 = inStorageRef.current.query.substring(3500, 7000);

    if (!_.isEmpty(additionalChange)) {
      setInStorage(additionalChange);
    }

    let sqlInfo = historyUpdate("T_AOTHERINFO", inStorageRef.current?.id ?? 0, appContext.user?.id ?? '');
    if (sqlInfo) _sqlInfosRef.current.push(sqlInfo);

    const sqlInfoList = getSQLInfoList(inStorage as TrackableEntity);
    sqlInfoList.push(..._sqlInfosRef.current);

    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveJobDetail(JobNames.Query, sqlInfoList);

    if(inStorageRef.current) {
      if(mode === FormType.ModeAdd) {
        const insresult = apiResult?.result.insertList.find(x => x.model === 'CustomizeReport');
        if(insresult) {
          inStorageRef.current.id = insresult.newId;
          setInStorage(inStorageRef.current);
        }
      }
      setSavedEntity(inStorageRef.current.id.toString(), mode === FormType.ModeAdd);
    }

    return apiResult;
  }

  interface QueryCheckData {
    u2Date:string,
  }
  
  async function checkDataAsync():Promise<boolean> {
    if(!inStorageRef.current) return true;    // Nothing to check

    let errorMsg = '';
    const reqParam:QueryCheckData = { u2Date: getOriginalValue(inStorageRef.current, "u2Date")};

    if (!inStorageRef.current.name) {
      errorMsg = appendLine(errorMsg, "* Please enter Name");
    }
    if (!inStorageRef.current.query) {
      errorMsg = appendLine(errorMsg, "* Please enter Query");
    }

    const chkResult:QueryCheckData = await appContext.api.checkData(JobNames.Query, inStorageRef.current.id > 0 ? inStorageRef.current.id : 0, reqParam);
    if(chkResult?.u2Date === "error") {
      await showGlobalOkDialog(<p>Can not save this record because this record has been changed while you are editing..</p>, false, null,'error');
      return false;
    }

    if (!errorMsg) {
      return true;
    } else {
      // Critical
      await showGlobalOkDialog(<p>Please check following information. (Specify the missing entries.)<br /><br />
        {errorMsg}
      </p>);
      return false;
    }
  }
  
  async function save() {
    if (!inStorage) {
      throw new Error('Invalid Query');
    }
  
    if(!inStorageRef.current?.isDirty) {
      if (onPopupClose) onPopupClose();
      return true;
    }
  
    const apiResult = await saveQuery();
    if (!apiResult || !apiResult.success) {
      return false;
    }
  
    if (onPopupClose) onPopupClose();
    return true;
  }

  if (!inStorage) {
    return null;
  }

  return (
    <SpecialReportInitializedImpl
      customizeReport={inStorage!}
      customizeReportRef={inStorageRef}
      setSpecialReport={setInStorage}
      onPopupClose={params.onPopupClose}
      isView={isView || false}
      save={save}
    />
  );
}

function SpecialReportInitializedImpl({
  customizeReport, customizeReportRef, setSpecialReport, onPopupClose, isView, save
}: SpecialReportLoadedDetailsViewParams) {
  useEffect(() => {
    focusTabableByIndex(13); // Default Focus -> Query Field
  }, []);

  const types = [
    {value: 0, label: ''},
    {value: 1, label: 'Date'},
    {value: 2, label: 'Float'},
    {value: 3, label: 'Integer'},
    {value: 4, label: 'Text'},
  ]

  function onChange(change: Partial<CustomizeReport>) {
    setSpecialReport(change);
  }

  return (
    <div className='report-popup'>
      <div style={{ display: 'flex' }}>
        <InlineInputControlRow label="Name" mode='w50' smallMargin>
          <div style={{width: '250px'}}><TextBox textId='special-name' disabled={isView} maxLength={250} value={customizeReport.name} onChange={e => onChange({name: e.target.value})} /></div>
        </InlineInputControlRow>

        <div style={{ marginLeft: '30px' }}>
          <div style={{ display: 'flex', marginLeft: '40px', textAlign: 'center' }}>
            <Label style={{ width: '95px', marginRight: '5px' }} label='Description' />
            <Label style={{ width: '80px' }} label='Type' />
          </div>

          <InlineInputControlRow mode='w40' smallMargin label='< #1 >'>
            <LeftRightSplit
              leftSize='100px'
              rightSize='80px'
              left={
                <div style={{ marginRight: '5px' }}>
                  <TextBox textId='special-desc1' disabled={isView} maxLength={25} value={customizeReport.description1} onChange={e => onChange({description1: e.target.value})} />
                </div>
              }
              right={
                <Select selectId='special-type1' items={types} disabled={isView} value={customizeReport.type1} onChange={e => onChange({type1: parseInt(e.target.value??0)})} />
              }
            />
          </InlineInputControlRow>

          <InlineInputControlRow mode='w40' smallMargin label='< #2 >'>
            <LeftRightSplit
              leftSize='100px'
              rightSize='80px'
              left={
                <div style={{ marginRight: '5px' }}>
                  <TextBox textId='special-desc2' disabled={isView} maxLength={25} value={customizeReport.description2} onChange={e => onChange({description2: e.target.value})} />
                </div>
              }
              right={
                <Select selectId='special-type2' items={types} disabled={isView} value={customizeReport.type2} onChange={e => onChange({type2: parseInt(e.target.value??0)})} />
              }
            />     
          </InlineInputControlRow>

          <InlineInputControlRow mode='w40' smallMargin label='< #3 >'>
            <LeftRightSplit
              leftSize='100px'
              rightSize='80px'
              left={
                <div style={{ marginRight: '5px' }}>
                  <TextBox textId='special-desc3' disabled={isView} maxLength={25} value={customizeReport.description3} onChange={e => onChange({description3: e.target.value})} />
                </div>
              }
              right={
                <Select selectId='special-type3' items={types} disabled={isView} value={customizeReport.type3} onChange={e => onChange({type3: parseInt(e.target.value??0)})} />
              }
            />      
          </InlineInputControlRow>
        </div>

        <div style={{ marginLeft: '30px' }}>
          <div style={{ display: 'flex', marginLeft: '40px', textAlign: 'center' }}>
            <Label style={{ width: '95px', marginRight: '5px' }} label='Description' />
            <Label style={{ width: '80px' }} label='Type' />
          </div>

          <InlineInputControlRow mode='w40' smallMargin label='< #4 >'>
            <LeftRightSplit
              leftSize='100px'
              rightSize='80px'
              left={
                <div style={{ marginRight: '5px' }}>
                  <TextBox textId='special-desc4' disabled={isView} maxLength={25} value={customizeReport.description4} onChange={e => onChange({description4: e.target.value})} />
                </div>
              }
              right={
                <Select selectId='special-type4' items={types} disabled={isView} value={customizeReport.type4} onChange={e => onChange({type4: parseInt(e.target.value??0)})} />
              }
            />
          </InlineInputControlRow>

          <InlineInputControlRow mode='w40' smallMargin label='< #5 >'>
            <LeftRightSplit
              leftSize='100px'
              rightSize='80px'
              left={
                <div style={{ marginRight: '5px' }}>
                  <TextBox textId='special-desc5' disabled={isView} maxLength={25} value={customizeReport.description5} onChange={e => onChange({description5: e.target.value})} />
                </div>
              }
              right={
                <Select selectId='special-type5' items={types} disabled={isView} value={customizeReport.type5} onChange={e => onChange({type5: parseInt(e.target.value??0)})} />
              }
            />     
          </InlineInputControlRow>

          <InlineInputControlRow mode='w40' smallMargin label='< #6 >'>
            <LeftRightSplit
              leftSize='100px'
              rightSize='80px'
              left={
                <div style={{ marginRight: '5px' }}>
                  <TextBox textId='special-desc6' disabled={isView} maxLength={25} value={customizeReport.description6} onChange={e => onChange({description6: e.target.value})} />
                </div>
              }
              right={
                <Select selectId='special-type6' items={types} disabled={isView} value={customizeReport.type6} onChange={e => onChange({type6: parseInt(e.target.value??0)})} />
              }
            />      
          </InlineInputControlRow>
        </div>
      </div>
      
      <div style={{ height: '405px', marginTop: '10px', marginBottom: '15px' }}>
        <InlineInputControlRow label="Query" mode='w50' smallMargin>
          <MultilineTextBox textId="special-query" style={{ height: '360px' }} disabled={isView} maxLength={7000} value={customizeReport.query} onChange={e => onChange({query: e.target.value})}/>
        </InlineInputControlRow>

        <InlineInputControlRow label="Order By" mode='w50' smallMargin>
          <MultilineTextBox textId="special-orderBy" style={{ height: '40px' }} disabled={isView} maxLength={250} value={customizeReport.orderBy} onChange={e => onChange({orderBy: e.target.value})}/>
        </InlineInputControlRow>
      </div>

      <div className='report-footer'>
        <SecondaryButton 
          name='special-save-button'
          focusable={true} 
          size="regular" 
          disabled={isView} 
          onClick={save}>
          SAVE
        </SecondaryButton>
        <SecondaryButton
          name='special-close-button'
          focusable={true}
          size="regular"
          onClick={onPopupClose}> 
          CLOSE
        </SecondaryButton>
      </div>
    </div>
  );
}

const SpecialReportDetails = ({ onPopupClose, selectedElementId, mode }
    : { onPopupClose?: () => void, selectedElementId: number, mode: FormType }) => {

  return (
    <SpecialReportImpl id={selectedElementId.toString()} onPopupClose={onPopupClose} mode={mode} 
      key={`specialReport-${mode}-${selectedElementId.toString()}-details`}/>
  );
};

export default SpecialReportDetails;